import {
  defineComponent,
  effectScope,
  getCurrentInstance,
  h,
  provide,
  watch,
  type App,
  type Component,
  type ComponentPublicInstance,
  type InjectionKey,
  type PropType,
} from 'vue';
import { createI18n, type I18nOptions } from 'vue-i18n';
import type { RouteRecordRaw } from 'vue-router';
import { i18nInjectionKey } from '@blpde/nx-ui';
import type { AppId } from '@/integration';
import shellI18n, { datetimeFormats } from './index';
import { appLocales, buildMessageTable } from './messages';

// Every hosted app translates with a table of its own (I-2284): its own texts
// plus the shared libraries', never another app's. Nothing in an app changes for
// that — vue-i18n finds its instance by injection (`useI18n()` injects
// `app.__VUE_I18N_SYMBOL__`), so providing the app's instance under that key
// hands it to every `useI18n()` below. The shell does so wherever an app's UI
// renders: the app's route (./loader.ts), its settings panels and showcase tabs,
// the embedded ERP and Drive components. Template `$t` needs the extra step in
// `scopeGlobalTranslation`.

function createAppI18n(id: AppId) {
  const i18n = createI18n({
    legacy: false,
    locale: shellI18n.global.locale.value,
    fallbackLocale: 'de',
    messages: buildMessageTable(appLocales[id]) as I18nOptions['messages'],
    datetimeFormats: { de: datetimeFormats, en: datetimeFormats },
  });
  // `sync`, so whoever watches the shell's locale (the command palette) already
  // reads the app's texts in the new language.
  watch(
    () => shellI18n.global.locale.value,
    (locale) => {
      i18n.global.locale.value = locale;
    },
    { flush: 'sync' }
  );
  return i18n;
}

export type AppI18n = ReturnType<typeof createAppI18n>;

// Detached: an instance first asked for inside a component's setup() must not
// stop with that component.
const scope = effectScope(true);
const instances = new Map<AppId, AppI18n>();

/** The app's own i18n instance, in the shell's language. Created on first use. */
export function appI18n(id: AppId): AppI18n {
  let instance = instances.get(id);
  if (!instance) {
    instance = scope.run(() => createAppI18n(id))!;
    instances.set(id, instance);
  }
  return instance;
}

/** `t` and `te` over one table, typed plainly enough to stand in for the shell's. */
export interface Translator {
  /** `plural`: the number that picks the form of a text with several (`a | b`). */
  t: (key: string, named?: Record<string, unknown>, plural?: number) => string;
  te: (key: string) => boolean;
}

/** The app's table for code outside its components (navbar, palette, hub, bell). */
export function appMessages(id: AppId): Translator {
  const { t, te } = appI18n(id).global;
  return {
    t: (key, named, plural) => {
      if (plural !== undefined) return t(key, named ?? {}, plural);
      return named ? t(key, named) : t(key);
    },
    te: (key) => te(key),
  };
}

/** `t` over the app's table (command palette, hub captions). */
export function appTranslator(id: AppId): Translator['t'] {
  return appMessages(id).t;
}

type I18nApp = App & { __VUE_I18N_SYMBOL__?: InjectionKey<unknown> };

function i18nKey(app: App | undefined): InjectionKey<unknown> {
  const key = (app as I18nApp | undefined)?.__VUE_I18N_SYMBOL__;
  if (!key) throw new Error('[connect] vue-i18n is not installed on this Vue app');
  return key;
}

/**
 * Hands the calling component's subtree the app's own table: to `useI18n()`
 * and to the kit (`i18nInjectionKey`). Call it in `setup()`.
 */
export function provideAppI18n(id: AppId): AppI18n {
  const i18n = appI18n(id);
  provide(i18nKey(getCurrentInstance()?.appContext.app), i18n);
  const { t, d, locale } = i18n.global;
  provide(i18nInjectionKey, { t, d, locale });
  return i18n;
}

/**
 * Renders its default slot with the app's own table. `app` is read once: key
 * the scope where it can change.
 */
export const ConnectAppI18nScope = defineComponent({
  name: 'ConnectAppI18nScope',
  props: {
    app: { type: String as PropType<AppId>, required: true },
  },
  setup(props, { slots }) {
    provideAppI18n(props.app);
    return () => slots.default?.();
  },
});

/** `component`, rendered with the app's own table; props, listeners and slots pass through. */
export function withAppI18n(id: AppId, component: Component): Component {
  return defineComponent({
    name: 'ConnectAppI18nScope',
    inheritAttrs: false,
    setup(_, { attrs, slots }) {
      provideAppI18n(id);
      return () => h(component, attrs, slots);
    },
  });
}

type LazyComponent = () => Promise<Component | { default: Component }>;

/** vue-router's own test: a lazily loaded route component is a plain function. */
function isLazy(component: unknown): component is LazyComponent {
  return (
    typeof component === 'function' &&
    !('displayName' in component) &&
    !('props' in component) &&
    !('__vccOpts' in component)
  );
}

/** The app's route, its component rendered with the app's own table. */
export function withAppI18nRoute(id: AppId, route: RouteRecordRaw): RouteRecordRaw {
  const component = route.component;
  if (!component) return route;
  return {
    ...route,
    component: isLazy(component)
      ? () =>
          component().then((loaded) =>
            withAppI18n(id, 'default' in loaded ? loaded.default : loaded)
          )
      : withAppI18n(id, component),
  } as RouteRecordRaw;
}

const GLOBAL_METHODS = ['t', 'rt', 'd', 'n', 'tm', 'te'] as const;

/**
 * vue-i18n binds the template helpers `$t`, `$d`, … to the instance installed
 * on the Vue app — the shell's — so they would read past an app's table. This
 * rebinds them to the instance the calling component sees: `this` is the
 * component, and its `provides` resolve like `inject()` does, up to the app-level
 * shell instance. Call it once, right after `app.use(i18n)`.
 */
export function scopeGlobalTranslation(app: App): void {
  const key = i18nKey(app) as symbol;
  for (const method of GLOBAL_METHODS) {
    Object.defineProperty(app.config.globalProperties, `$${method}`, {
      configurable: true,
      writable: true,
      value(this: ComponentPublicInstance | undefined, ...args: unknown[]) {
        const provides = (this?.$ as unknown as { provides?: Record<symbol, unknown> } | undefined)
          ?.provides;
        const i18n = (provides?.[key] as typeof shellI18n | undefined) ?? shellI18n;
        return (i18n.global[method] as (...rest: unknown[]) => unknown)(...args);
      },
    });
  }
}
